import {createSlice, isAnyOf, type Update} from '@reduxjs/toolkit';
import {
    type CompPass,
    type CompPassBatchLinkDetail,
    type CompPassRedemptionRow,
} from 'api/comp-passes/comp-pass.types';
import {type UpcomingEvent} from 'api/events/events.types';
import {type CompPassesEnabledType} from 'api/facilities/facilities.types';
import {type PaginationPage} from 'api/api.types';
import {apiDateFormat, displayDateFormat} from 'utils/date-time';
import dayjs from 'utils/dayjs';
import {
    type CompPassConfirmationErrors,
    type IndividualError,
    type RecipientError,
    type TransientTimeFrameErrors,
} from 'pages/comp-pass/components/comp-pass-wizard/comp-pass-wizard.types';
import {
    ADD_RECIPIENTS_METHOD,
    DEFAULT_EVENT_OFFSET,
    PASS_TYPE,
} from 'pages/comp-pass/components/comp-pass-wizard/utils';
import {type SelectOption} from 'pages/comp-pass/comp-pass.types';

import {
    confirmAndSendCompPasses,
    confirmAndCreateSharedCompPassLinks,
    decrementCreationStep,
    deleteCompPassData,
    getCompPassesData,
    getCompPassBatchLinkDetailsData,
    getCompPassRedemptionDetailsData,
    getCompPassSummaryCountData,
    getPassCreationHistory,
    incrementCreationStep,
    resetCompPassUsage,
    resetCreationFlow,
    setActiveTabIndex,
    setCreationMethod,
    setCreationStep,
    setGlobalError,
    setPassType,
    setSharedLinkAvailablePasses,
    updateCompPassesData,
    updateListingFilters,
} from 'pages/comp-pass/store/comp-pass-actions';
import {
    compPassBatch,
    compPassRecipient,
    passListAdapter,
    transientTimeFrame,
} from 'pages/comp-pass/store/comp-pass-adapters';
import {
    deleteRecipient,
    deleteUploadedCsvFile,
    setCsvUploadError,
    setDisclosureAcknowledged,
    updateAndValidateManualCompPassRecipients,
    validateAndAddCsvCompPassRecipients,
} from 'pages/comp-pass/store/recipient-actions';
import {
    addTransientTimeFrame,
    forceTransientErrors,
    removeTransientTimeFrame,
    updateAndValidateTransientTimeFrames,
    updateTransientTimeFrame,
} from 'pages/comp-pass/store/transient-time-frame-actions';
import {
    getEventFilterOptions,
    getUpcomingEventsData,
    updateEventFilters,
    updateEventSelection,
} from 'pages/comp-pass/store/event-selection-actions';
import {
    setEventEndsOffset,
    setEventOffsetsEnabled,
    setEventStartsOffset,
} from 'pages/comp-pass/store/event-offsets-actions';
import {compPassEventsAdapter} from 'pages/comp-pass/store/event-selection-adapters';

const now = dayjs();

export const initialFromDate = now.format(displayDateFormat);
export const initialToDate = now.add(90, 'day').format(displayDateFormat);

export interface CompPassState {
    activeTabIndex: number;
    aggregateCounts: {
        counts: {
            day: number;
            month: number;
            year: number;
        };
        date: string;
    };
    filterOptions: {
        compPassBatchId?: string;
        destinationIds?: SelectOption[];
        email?: string;
        eventTypeIds?: SelectOption[];
        fromDate: string;
        isLoading: boolean;
        toDate: string;
    };
    isLoading: boolean;
    passBatches: {
        batches: ReturnType<typeof compPassBatch.getInitialState>;
        isLoading: boolean;
        meta: PaginationPage;
    };
    passBatchLinkDetails: {
        batchId: number | null;
        data: CompPassBatchLinkDetail[];
        error: unknown;
        isLoading: boolean;
        meta: PaginationPage;
    };
    passCreation: {
        addRecipients: {
            fileName?: string;
            isDisclosureAcknowledged: boolean;
            method: string;
            recipients: ReturnType<typeof compPassRecipient.getInitialState>;
            sharedLink: {
                availablePasses: string;
            };
        };
        errors: {
            globalError: string | null;
            addRecipients: RecipientError;
            confirmation: CompPassConfirmationErrors;
            selectEvents: IndividualError;
            transientTimeFrames: TransientTimeFrameErrors;
            displayAllTransientErrors: boolean;
        };
        passType: CompPassesEnabledType;
        transientTimeFrames: ReturnType<
            typeof transientTimeFrame.getInitialState
        >;
        selectEvents: {
            appliedFilters: {
                destinationIds?: number[];
                eventTypeIds?: number[];
                fromDate?: string;
                numFiltersApplied: number;
                toDate?: string;
            };
            data: {
                events: ReturnType<
                    typeof compPassEventsAdapter.getInitialState
                >;
                isLoading: boolean;
                meta: PaginationPage;
            };
            selectedEvents: UpcomingEvent[];
            /**
             * Custom offsets applied across all selected events. Event-flow
             * only; when disabled the pricing-rule offsets are used. Field names
             * match the API contract (`event_starts_offset`/`event_ends_offset`).
             */
            offsets: {
                enabled: boolean;
                eventStartsOffset: string;
                eventEndsOffset: string;
            };
        };
        step: number;
    };
    passListing: {
        data: ReturnType<typeof passListAdapter.getInitialState>;
        filters: {
            compPassBatchId?: number;
            destinationIds?: number[];
            email?: string;
            eventTypeIds?: number[];
            fromDate: string;
            numFiltersApplied: number;
            toDate: string;
        };
        isLoading: boolean;
        meta: PaginationPage;
    };
    passUsage: {
        /** The comp pass whose usage-details flyout is open. */
        activeCompPassId: number | null;
        /** Entry/exit rows, accumulated across infinite-scroll pages. */
        rows: CompPassRedemptionRow[];
        /** The "received at" timestamp from the payload's `data.asOf`. */
        asOf: string | null;
        /** Pagination for the redemption-details rows. */
        meta: PaginationPage;
        isLoading: boolean;
        /** Set when a redemption-details fetch fails (non-aborted). */
        isError: boolean;
    };
}

export const initialState: CompPassState = {
    activeTabIndex: 0,
    aggregateCounts: {
        counts: {
            day: 0,
            month: 0,
            year: 0,
        },
        date: now.format(apiDateFormat),
    },
    filterOptions: {
        fromDate: initialFromDate,
        toDate: initialToDate,
        destinationIds: [],
        eventTypeIds: [],
        isLoading: false,
    },
    isLoading: false,
    passBatches: {
        batches: compPassBatch.getInitialState(),
        isLoading: false,
        meta: {
            nextPage: null,
            page: 1,
            pages: 1,
            perPage: 25,
            prevPage: null,
            total: 0,
        },
    },
    passBatchLinkDetails: {
        batchId: null,
        data: [],
        error: null,
        isLoading: false,
        meta: {
            nextPage: null,
            page: 1,
            pages: 1,
            perPage: 25,
            prevPage: null,
            total: 0,
        },
    },
    passCreation: {
        addRecipients: {
            fileName: null,
            isDisclosureAcknowledged: false,
            method: ADD_RECIPIENTS_METHOD.UPLOAD,
            recipients: compPassRecipient.getInitialState(),
            sharedLink: {
                availablePasses: '',
            },
        },
        errors: {
            globalError: null,
            addRecipients: null,
            confirmation: null,
            selectEvents: null,
            transientTimeFrames: null,
            displayAllTransientErrors: false,
        },
        passType: PASS_TYPE.EVENT,
        transientTimeFrames: transientTimeFrame.getInitialState(),
        selectEvents: {
            appliedFilters: {
                destinationIds: [],
                eventTypeIds: [],
                fromDate: initialFromDate,
                numFiltersApplied: 0,
                toDate: initialToDate,
            },
            data: {
                events: compPassEventsAdapter.getInitialState(),
                isLoading: false,
                meta: {
                    nextPage: null,
                    page: 1,
                    pages: 1,
                    perPage: 25,
                    total: 25,
                },
            },
            selectedEvents: [],
            offsets: {
                enabled: false,
                eventStartsOffset: DEFAULT_EVENT_OFFSET,
                eventEndsOffset: DEFAULT_EVENT_OFFSET,
            },
        },
        step: 0,
    },
    passListing: {
        data: passListAdapter.getInitialState(),
        filters: {
            compPassBatchId: null,
            destinationIds: [],
            email: '',
            eventTypeIds: [],
            fromDate: initialFromDate,
            numFiltersApplied: 0,
            toDate: initialToDate,
        },
        isLoading: false,
        meta: {
            nextPage: null,
            page: 1,
            pages: 1,
            perPage: 25,
            prevPage: null,
            total: 25,
        },
    },
    passUsage: {
        activeCompPassId: null,
        rows: [],
        asOf: null,
        meta: {
            nextPage: null,
            page: 1,
            pages: 1,
            perPage: 25,
            prevPage: null,
            total: 0,
        },
        isLoading: false,
        isError: false,
    },
};

const compPassSlice = createSlice({
    name: 'compPass',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(setActiveTabIndex, (state, action) => {
                state.activeTabIndex = action.payload;
            })
            .addCase(getCompPassSummaryCountData.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(getCompPassSummaryCountData.fulfilled, (state, action) => {
                state.isLoading = false;

                state.aggregateCounts = action.payload;
            })
            .addCase(getPassCreationHistory.pending, (state) => {
                state.passBatches.isLoading = true;
            })
            .addCase(getPassCreationHistory.fulfilled, (state, action) => {
                compPassBatch.setAll(
                    state.passBatches.batches,
                    action.payload.data.results
                );
                state.passBatches.meta = action.payload.meta;
                state.passBatches.isLoading = false;
            })
            .addCase(
                getCompPassBatchLinkDetailsData.pending,
                (state, action) => {
                    state.passBatchLinkDetails.batchId =
                        action.meta.arg.batchId;
                    state.passBatchLinkDetails.error = null;
                    state.passBatchLinkDetails.isLoading = true;
                }
            )
            .addCase(
                getCompPassBatchLinkDetailsData.fulfilled,
                (state, action) => {
                    state.passBatchLinkDetails.data =
                        action.payload.data.results;
                    state.passBatchLinkDetails.meta = action.payload.meta;
                    state.passBatchLinkDetails.isLoading = false;
                }
            )
            .addCase(
                getCompPassBatchLinkDetailsData.rejected,
                (state, action) => {
                    if (action.meta.aborted) {
                        return;
                    }

                    state.passBatchLinkDetails.error = action.payload;
                    state.passBatchLinkDetails.isLoading = false;
                }
            )
            .addCase(getCompPassesData.fulfilled, (state, {payload}) => {
                passListAdapter.setAll(
                    state.passListing.data,
                    payload.data.results
                );
                state.passListing.meta = payload.meta;
                state.passListing.isLoading = false;
            })
            .addCase(getCompPassesData.pending, (state) => {
                state.passListing.isLoading = true;
            })
            .addCase(getCompPassesData.rejected, (state, action) => {
                if (action.meta.aborted) {
                    return;
                }

                state.passListing.isLoading = false;
            })
            .addCase(
                getCompPassRedemptionDetailsData.pending,
                (state, action) => {
                    state.passUsage.isLoading = true;
                    state.passUsage.isError = false;
                    state.passUsage.activeCompPassId =
                        action.meta.arg.compPassId;
                }
            )
            .addCase(
                getCompPassRedemptionDetailsData.fulfilled,
                (state, {payload, meta}) => {
                    const {data} = payload;
                    const isNextPage = meta.arg.page && meta.arg.page > 1;

                    state.passUsage.rows = isNextPage
                        ? [...state.passUsage.rows, ...data.results]
                        : data.results;
                    state.passUsage.asOf = data.asOf;
                    state.passUsage.meta = payload.meta;
                    state.passUsage.isLoading = false;
                    state.passUsage.isError = false;
                }
            )
            .addCase(
                getCompPassRedemptionDetailsData.rejected,
                (state, action) => {
                    if (action.meta.aborted) {
                        return;
                    }

                    state.passUsage.isLoading = false;
                    state.passUsage.isError = true;
                }
            )
            .addCase(resetCompPassUsage, (state) => {
                state.passUsage = initialState.passUsage;
            })
            .addCase(updateCompPassesData.fulfilled, (state, {payload}) => {
                const {response, updates} = payload;
                const succeeded = new Set(response.updatedCompPassIds);
                const changes: Update<CompPass>[] = updates
                    .filter(({compPassId}) => succeeded.has(compPassId))
                    .map(({compPassId, ...rest}) => ({
                        id: compPassId,
                        changes: rest,
                    }));

                passListAdapter.updateMany(state.passListing.data, changes);
            })
            .addCase(deleteCompPassData.fulfilled, (state, {payload}) => {
                passListAdapter.removeOne(state.passListing.data, payload);
                state.passListing.isLoading = false;
            })
            .addCase(deleteCompPassData.pending, (state) => {
                state.passListing.isLoading = true;
            })
            .addCase(deleteCompPassData.rejected, (state) => {
                state.passListing.isLoading = false;
            })
            .addCase(updateListingFilters, (state, action) => {
                state.passListing.filters = action.payload;
            })
            // Creation Step Actions
            .addCase(incrementCreationStep, (state) => {
                state.passCreation.step++;
            })
            .addCase(decrementCreationStep, (state) => {
                state.passCreation.step--;
            })
            .addCase(setCreationStep, (state, action) => {
                state.passCreation.step = action.payload;
            })
            .addCase(resetCreationFlow, (state) => {
                state.passCreation = initialState.passCreation;
            })
            .addCase(setGlobalError, (state, {payload}) => {
                state.passCreation.errors.globalError = payload;
            })
            // Event Selection Actions
            .addCase(updateEventFilters, (state, action) => {
                state.passCreation.selectEvents.appliedFilters = action.payload;
            })
            .addCase(getEventFilterOptions.pending, (state) => {
                state.filterOptions.isLoading = true;
            })
            .addCase(getEventFilterOptions.fulfilled, (state, {payload}) => {
                const {venueIds, eventTypeIds} = payload;

                state.filterOptions = {
                    ...state.filterOptions,
                    destinationIds: venueIds,
                    eventTypeIds,
                    isLoading: false,
                };
            })
            .addCase(getUpcomingEventsData.fulfilled, (state, {payload}) => {
                const {data, meta} = payload;

                compPassEventsAdapter.setAll(
                    state.passCreation.selectEvents.data.events,
                    data
                );
                state.passCreation.selectEvents.data.isLoading = false;
                state.passCreation.selectEvents.data.meta = meta;
            })
            .addCase(getUpcomingEventsData.pending, (state) => {
                state.passCreation.selectEvents.data.isLoading = true;
            })
            .addCase(getUpcomingEventsData.rejected, (state, action) => {
                if (action.meta.aborted) {
                    return;
                }

                state.passCreation.selectEvents.data.isLoading = false;
            })
            .addCase(updateEventSelection, (state, {payload}) => {
                state.passCreation.selectEvents.selectedEvents = payload;
                state.passCreation.errors.globalError = null;
            })
            // Pass Type
            .addCase(setPassType, (state, {payload}) => {
                state.passCreation.passType = payload;
                state.passCreation.errors.globalError = null;

                state.passCreation.selectEvents.selectedEvents = [];
                state.passCreation.selectEvents.offsets =
                    initialState.passCreation.selectEvents.offsets;
                state.passCreation.errors.selectEvents = null;
                transientTimeFrame.removeAll(
                    state.passCreation.transientTimeFrames
                );
                state.passCreation.errors.transientTimeFrames = null;
                state.passCreation.errors.displayAllTransientErrors = false;
            })
            // Custom Event Offsets
            .addCase(setEventOffsetsEnabled, (state, {payload}) => {
                state.passCreation.selectEvents.offsets.enabled = payload;

                // AC: turning the toggle on defaults to 1h before / 1h after.
                if (payload) {
                    state.passCreation.selectEvents.offsets.eventStartsOffset =
                        DEFAULT_EVENT_OFFSET;
                    state.passCreation.selectEvents.offsets.eventEndsOffset =
                        DEFAULT_EVENT_OFFSET;
                }

                state.passCreation.errors.globalError = null;
            })
            .addCase(setEventStartsOffset, (state, {payload}) => {
                state.passCreation.selectEvents.offsets.eventStartsOffset =
                    payload;
                state.passCreation.errors.globalError = null;
            })
            .addCase(setEventEndsOffset, (state, {payload}) => {
                state.passCreation.selectEvents.offsets.eventEndsOffset =
                    payload;
                state.passCreation.errors.globalError = null;
            })
            // Transient Time Frames
            .addCase(addTransientTimeFrame, (state, {payload}) => {
                transientTimeFrame.addOne(
                    state.passCreation.transientTimeFrames,
                    payload
                );
                state.passCreation.errors.displayAllTransientErrors = false;
                state.passCreation.errors.globalError = null;
            })
            .addCase(updateTransientTimeFrame, (state, {payload}) => {
                transientTimeFrame.updateOne(
                    state.passCreation.transientTimeFrames,
                    {id: payload.id, changes: payload.changes}
                );
                state.passCreation.errors.displayAllTransientErrors = false;
                state.passCreation.errors.globalError = null;

                // Drop the edited row's stale errors so a "required" error
                // can't flash when its field receives a value; the debounced
                // validation recomputes them.
                const existingErrors =
                    state.passCreation.errors.transientTimeFrames;

                if (existingErrors && payload.id in existingErrors) {
                    delete existingErrors[payload.id];

                    state.passCreation.errors.transientTimeFrames =
                        Object.keys(existingErrors).length > 0
                            ? existingErrors
                            : null;
                }
            })
            .addCase(removeTransientTimeFrame, (state, {payload}) => {
                transientTimeFrame.removeOne(
                    state.passCreation.transientTimeFrames,
                    payload
                );
                state.passCreation.errors.displayAllTransientErrors = false;
                state.passCreation.errors.globalError = null;

                const existingErrors =
                    state.passCreation.errors.transientTimeFrames;

                if (existingErrors && payload in existingErrors) {
                    delete existingErrors[payload];

                    state.passCreation.errors.transientTimeFrames =
                        Object.keys(existingErrors).length > 0
                            ? existingErrors
                            : null;
                }
            })
            .addCase(forceTransientErrors, (state) => {
                state.passCreation.errors.displayAllTransientErrors = true;
            })
            .addCase(
                updateAndValidateTransientTimeFrames.fulfilled,
                (state, {payload}) => {
                    transientTimeFrame.setAll(
                        state.passCreation.transientTimeFrames,
                        payload
                    );
                    state.passCreation.errors.transientTimeFrames = null;
                    state.passCreation.errors.globalError = null;
                }
            )
            .addCase(
                updateAndValidateTransientTimeFrames.rejected,
                (state, {payload}) => {
                    if (!payload) {
                        return;
                    }

                    transientTimeFrame.setAll(
                        state.passCreation.transientTimeFrames,
                        payload.rows
                    );
                    state.passCreation.errors.transientTimeFrames =
                        payload.errors;
                }
            )
            // Add Recipients Actions
            .addCase(setCreationMethod, (state, {payload}) => {
                state.passCreation.addRecipients.method = payload;

                compPassRecipient.removeAll(
                    state.passCreation.addRecipients.recipients
                );
                state.passCreation.addRecipients.fileName = null;
                state.passCreation.addRecipients.sharedLink.availablePasses =
                    '';
                state.passCreation.errors.addRecipients = null;
                state.passCreation.errors.globalError = null;
            })
            .addCase(setSharedLinkAvailablePasses, (state, {payload}) => {
                state.passCreation.addRecipients.sharedLink.availablePasses =
                    payload;
                state.passCreation.errors.addRecipients = null;
                state.passCreation.errors.globalError = null;
            })
            .addCase(setDisclosureAcknowledged, (state, {payload}) => {
                state.passCreation.addRecipients.isDisclosureAcknowledged =
                    payload;
                state.passCreation.errors.globalError = null;
            })
            // Manual Step
            .addCase(
                updateAndValidateManualCompPassRecipients.fulfilled,
                (state, {payload}) => {
                    compPassRecipient.upsertMany(
                        state.passCreation.addRecipients.recipients,
                        payload
                    );

                    state.passCreation.errors.addRecipients = null;
                    state.passCreation.errors.globalError = null;
                }
            )
            .addCase(
                updateAndValidateManualCompPassRecipients.rejected,
                (state, {payload}) => {
                    const {errors, recipients} = payload;

                    state.passCreation.errors.addRecipients = errors;
                    compPassRecipient.upsertMany(
                        state.passCreation.addRecipients.recipients,
                        recipients
                    );
                }
            )
            .addCase(deleteRecipient.fulfilled, (state, {payload}) => {
                const {addRecipientsErrors, recipients} = payload;

                compPassRecipient.setAll(
                    state.passCreation.addRecipients.recipients,
                    recipients
                );

                state.passCreation.errors.addRecipients = addRecipientsErrors;
            })
            // CSV Upload
            .addCase(
                validateAndAddCsvCompPassRecipients.fulfilled,
                (state, {payload}) => {
                    const {recipients, fileName} = payload;

                    compPassRecipient.setAll(
                        state.passCreation.addRecipients.recipients,
                        recipients
                    );
                    state.passCreation.addRecipients.fileName = fileName;

                    state.passCreation.errors.addRecipients = null;
                    state.passCreation.errors.globalError = null;
                }
            )
            .addCase(deleteUploadedCsvFile, (state) => {
                compPassRecipient.removeAll(
                    state.passCreation.addRecipients.recipients
                );

                state.passCreation.addRecipients.fileName = null;
            })
            .addCase(confirmAndSendCompPasses.fulfilled, (state, {payload}) => {
                const {data, meta} = payload;

                compPassBatch.setAll(state.passBatches.batches, data);
                state.passBatches.isLoading = false;
                state.passBatches.meta = meta;
            })
            .addCase(confirmAndSendCompPasses.rejected, (state, {payload}) => {
                state.passBatches.isLoading = false;
                state.passCreation.errors.confirmation = payload;
            })
            .addCase(confirmAndSendCompPasses.pending, (state) => {
                state.passBatches.isLoading = true;
            })
            .addCase(
                confirmAndCreateSharedCompPassLinks.fulfilled,
                (state, {payload}) => {
                    const {data, meta} = payload;

                    compPassBatch.setAll(state.passBatches.batches, data);
                    state.passBatches.isLoading = false;
                    state.passBatches.meta = meta;
                }
            )
            .addCase(
                confirmAndCreateSharedCompPassLinks.rejected,
                (state, {payload}) => {
                    state.passBatches.isLoading = false;
                    state.passCreation.errors.confirmation = payload;
                }
            )
            .addCase(confirmAndCreateSharedCompPassLinks.pending, (state) => {
                state.passBatches.isLoading = true;
            })
            .addMatcher(
                isAnyOf(
                    validateAndAddCsvCompPassRecipients.rejected,
                    setCsvUploadError
                ),
                (state, {payload}) => {
                    state.passCreation.errors.addRecipients = payload;
                }
            );
    },
});

export default compPassSlice.reducer;
